/* eslint-disable prefer-rest-params */

import { useEffect, useMemo, useState } from "react";
import "./index.scss";
import "./layout.scss";
import { Link, navigate } from "gatsby";
import cx from "classnames";
import { AppBar, Box, Button, Container, Grid, Typography } from "@wealthx/ui";
import { useCompanyInfo } from "../../../hooks";
import { BUSINESS_TYPE, DEFAULT_BROKER_DETAILS } from "../../../commons/enum";
import { useLoanApplicationLayout } from "../../../context/loan-application-layout-context";
import StandardButton from "../../../components/StandardButton/index.tsx";
import { GunnLogo, GunnLogoShorten, GunnLogoWhite, NoImage } from "../../../images";
import { primaryColor } from "../../../scss/colors.module.scss";
import { logout } from "../../../utils/auth";

const PREFIX = "LoanApplicationLayout";

const classes = {
  appBar: `${PREFIX}-appBar`,
  appBarContent: `${PREFIX}-appBarContent`,
  content: `${PREFIX}-content`,
  brokerInformationSection: `${PREFIX}-brokerInformationSection`,
  brokerInformationContainerTitle: `${PREFIX}-brokerInformationContainerTitle`,
  underlineHighlighted: `${PREFIX}-underlineHighlighted`,
  button: `${PREFIX}-button`,
  clientLoginButton: `${PREFIX}-clientLoginButton`,
  quickNavigationButtonsContainer: `${PREFIX}-quickNavigationButtonsContainer`,
  footer: `${PREFIX}-footer`,
  footerNavigation: `${PREFIX}-footerNavigation`,
  footerLinkContainer: `${PREFIX}-footerLinkContainer`,
  footerButton: `${PREFIX}-footerButton`,
  contactInformationWrapper: `${PREFIX}-contactInformationWrapper`,
  contactInformationContainer: `${PREFIX}-contactInformationContainer`,
  workingHours: `${PREFIX}-workingHours`,
  title: `${PREFIX}-title`,
  contactInformation: `${PREFIX}-contactInformation`,
  siteBuildInformation: `${PREFIX}-siteBuildInformation`,
  companyInformation: `${PREFIX}-companyInformation`,
  licenseInformation: `${PREFIX}-licenseInformation`,
  siteBuiltBy: `${PREFIX}-siteBuiltBy`,
  mobileNoPadding: `${PREFIX}-mobileNoPadding`,
  hiddenOnMobile: `${PREFIX}-hiddenOnMobile`,
  shownOnMobile: `${PREFIX}-shownOnMobile`,
  marginBottom16: `${PREFIX}-marginBottom16`,
  brokerInformationContainer: `${PREFIX}-brokerInformationContainer`,
  brokerInformationWrapper: `${PREFIX}-brokerInformationWrapper`,
  logoButton: `${PREFIX}-logoButton`,
};

function LoanApplicationLayout({ children, location }) {
  const { data: companyInfo = {} } = useCompanyInfo();
  const creditGuideFileUrl = companyInfo?.homepageDetails?.creditGuideFileUrl || "";
  const [isSavingAndLoggingOut, setIsSavingAndLoggingOut] = useState(false);

  const { pathname } = location;
  const { isRouteGateActive, saveCallback } = useLoanApplicationLayout();
  // On `/loan-application`, the page can immediately redirect to a pre-loan route.
  // Hide this layout chrome during that decision to prevent a visible flash.
  const shouldHideLayout = useMemo(
    () => pathname === "/loan-application/" && isRouteGateActive,
    [pathname, isRouteGateActive]
  );
  const shouldShowBrokerInfo = !pathname.includes("/privacy"); // do not show broker info in privacy page
  const phoneNumber =
    companyInfo?.businessPhoneNumber ||
    companyInfo?.adminDetails?.user?.phoneNumber ||
    DEFAULT_BROKER_DETAILS.phoneNumber;
  const email =
    companyInfo?.customerSupportEmail ||
    companyInfo?.adminDetails?.user?.email ||
    DEFAULT_BROKER_DETAILS.supportEmail;

  useEffect(() => {
    if (!companyInfo?.businessType) {
      return;
    }
    if (companyInfo.businessType !== BUSINESS_TYPE.BROKER) {
      window.location.href = "/";
    }
  }, [companyInfo?.businessType]);

  useEffect(() => {
    // Hotjar tracking script
    const hotjarId = process.env.GATSBY_HOTJAR_ID;
    const hotjarVersion = process.env.GATSBY_HOTJAR_VERSION;
    if (!hotjarId || !hotjarVersion) {
      return;
    }

    if (typeof window !== "undefined" && !window.hj) {
      const h = window;
      const o = document;
      const t = "https://static.hotjar.com/c/hotjar-";
      const j = ".js?sv=";
      h.hj =
        h.hj ||
        // eslint-disable-next-line func-names
        function () {
          (h.hj.q = h.hj.q || []).push(arguments);
        };
      h._hjSettings = {
        hjid: parseInt(hotjarId, 10),
        hjsv: parseInt(hotjarVersion, 10),
      };
      const head = o.getElementsByTagName("head")[0];
      const script = o.createElement("script");
      script.async = true;
      script.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      head.appendChild(script);
    }
  }, []);

  if (shouldHideLayout) {
    return children;
  }

  return (
    <div
      className="loan-app-page-container"
      style={{ "--loan-application-layout-primary-color": primaryColor }}
    >
      <AppBar color="transparent" position="static" className={classes.appBar}>
        <Grid container size={12}>
          <Grid size={1} />
          <Grid container className={classes.appBarContent} size={10}>
            <Box className={classes.logoButton} onClick={() => navigate("/loan-application")}>
              <img
                src={companyInfo?.theme?.logoDarkUrl || GunnLogo}
                alt="app-logo"
                className="loan-app-logo-desktop"
              />
              <img
                src={
                  companyInfo?.theme?.iconDarkUrl ||
                  companyInfo?.theme?.logoDarkUrl ||
                  GunnLogoShorten
                }
                alt="app-logo"
                className="loan-app-logo-mobile"
              />
            </Box>
            <Box
              className={cx(classes.quickNavigationButtonsContainer, classes.mobileNoPadding)}
              sx={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <StandardButton
                variant="outlined"
                loading={isSavingAndLoggingOut}
                onClick={async () => {
                  setIsSavingAndLoggingOut(true);
                  try {
                    if (saveCallback) {
                      try {
                        // Race the save callback against a 2 second timeout
                        const timeoutPromise = new Promise((resolve) => {
                          setTimeout(() => resolve(), 2000);
                        });
                        await Promise.race([saveCallback(), timeoutPromise]);
                      } catch {
                        // Silently fail - user can still log out
                      }
                    }
                    await logout("/login");
                  } finally {
                    setIsSavingAndLoggingOut(false);
                  }
                }}
                classes={{
                  borderColor: "var(--secondaryColor, #162029)",
                  color: "#131313",
                  textTransform: "none",
                  letterSpacing: "0.4px",
                  padding: "8px 22px",
                  "&:hover": {
                    borderColor: "var(--secondaryColor, #162029)",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Save & Log Out
              </StandardButton>
              <Button
                variant="contained"
                onClick={() => {
                  navigate("/");
                }}
                className={cx(
                  classes.button,
                  "loan-app-font-size-14",
                  "loan-app-font-weight-semibold"
                )}
                sx={{
                  backgroundColor: "var(--secondaryColor, #162029)",
                  color: "white !important",
                  textTransform: "none",
                  letterSpacing: "0.4px",
                  padding: "8px 22px",
                  "&:hover": {
                    backgroundColor: "var(--secondaryColor, #162029)",
                  },
                }}
              >
                Login App
              </Button>
            </Box>
          </Grid>
          <Grid size={1} />
        </Grid>
      </AppBar>
      <Grid container className={classes.content}>
        <Grid size={1} />
        <Grid size={10}>
          {shouldShowBrokerInfo && (
            <Container
              maxWidth={false}
              className={cx(classes.brokerInformationSection, "loan-app-desktop-display")}
            >
              <Grid container>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="flex-start"
                  spacing={2}
                  size={{
                    xs: 5,
                    md: 6,
                  }}
                >
                  <Grid>
                    <Typography className={classes.brokerInformationContainerTitle}>
                      Quick Online Application, <br /> That Get’s You Answers Fast!
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography component="span" className="loan-app-font-size-18">
                      <Typography
                        component="span"
                        variant="inherit"
                        className={cx(classes.underlineHighlighted, "loan-app-font-size-18")}
                      >
                        (This does not
                      </Typography>{" "}
                      effect your credit score)
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  size={{
                    xs: 7,
                    md: 6,
                  }}
                >
                  <Box className={classes.brokerInformationContainer}>
                    <Box className={classes.brokerInformationWrapper}>
                      <Box display="flex" flexDirection="column" alignItems="flex-end" rowGap="8px">
                        <Typography className="loan-app-font-size-18">
                          <strong>
                            {companyInfo?.adminDetails?.user?.firstName}{" "}
                            {companyInfo?.adminDetails?.user?.lastName}
                          </strong>
                        </Typography>
                        <Typography className="loan-app-font-size-18">Mortgage Broker</Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" alignItems="flex-end" rowGap="8px">
                        <Typography className="loan-app-font-size-18">E: {email}</Typography>
                        <Typography className="loan-app-font-size-18">M: {phoneNumber}</Typography>
                      </Box>
                    </Box>
                    <Box width="fit-content">
                      <div className="loan-app-broker-avatar-wrapper">
                        <img
                          src={companyInfo?.adminDetails?.imageUrl || NoImage}
                          alt="broker-avater"
                          className="loan-app-broker-avatar"
                        />
                        <div className="loan-app-broker-avatar-shadow-box__inner" />
                        <div className="loan-app-broker-avatar-shadow-box__outer" />
                      </div>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          )}
          <Grid container className="loan-app-content-container">
            {children}
          </Grid>
        </Grid>
        <Grid size={1} />
      </Grid>
      <Grid container>
        <Grid container className={classes.footer} size={12}>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
          <Grid
            container
            size={{
              xs: 8,
              sm: 10,
            }}
          >
            <Grid
              container
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Box className={classes.footerNavigation}>
                <img
                  src={companyInfo?.theme?.logoUrl || GunnLogoWhite}
                  alt="app-logo-white"
                  className="loan-app-logo-white-desktop"
                />
                <img
                  src={companyInfo?.theme?.iconUrl || companyInfo?.theme?.logoUrl || GunnLogoWhite}
                  alt="app-logo-white"
                  className="loan-app-logo-white-mobile"
                />
                <Box className={cx(classes.footerLinkContainer)}>
                  <Button component={Link} to="/loan-application" className={classes.footerButton}>
                    Home
                  </Button>
                  <Button component={Link} to="/signup" className={classes.footerButton}>
                    Signup
                  </Button>
                  <Button component={Link} to="/login" className={classes.footerButton}>
                    Login
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid
              container
              className={classes.contactInformationWrapper}
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Box className={classes.contactInformationContainer}>
                <Box className={cx(classes.shownOnMobile, classes.marginBottom16)}>
                  <Typography className={cx("loan-app-font-size-18", "loan-app-text-white")}>
                    Just a phone call away <br /> if you need help.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={cx(
                      classes.title,
                      "loan-app-font-size-24",
                      "loan-app-text-white",
                      classes.hiddenOnMobile
                    )}
                  >
                    Get In Touch
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Weekdays 8 AM - 8 PM AEST
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Saturday 10 AM - 4 PM
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Sunday We Enjoy Australia
                  </Typography>
                </Box>
                <Box className={classes.contactInformation}>
                  <Typography className={cx("loan-app-font-size-18", "loan-app-text-white")}>
                    <b>Call:</b> {phoneNumber}
                  </Typography>
                  <Typography className={cx("loan-app-font-size-18", "loan-app-text-white")}>
                    <b>Email:</b> {email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
        </Grid>
        <Grid container className={classes.siteBuildInformation} size={12}>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
          <Grid
            container
            className={classes.companyInformation}
            size={{
              xs: 8,
              sm: 7,
            }}
          >
            <Typography
              className={cx(
                classes.licenseInformation,
                "loan-app-font-size-12",
                "loan-app-font-weight-light",
                "loan-app-text-white",
                "loan-app-text-uppercase",
                classes.hiddenOnMobile
              )}
            >
              ALL APPLICANTS ARE SUBJECT TO ELIGIBILITY AND ASSESSMENT{" "}
              {`${companyInfo?.name || DEFAULT_BROKER_DETAILS.companyName}`} PTY LTD | ABN{" "}
              {`${companyInfo?.companyNumber || DEFAULT_BROKER_DETAILS.companyNumber}`} | AUSTRALIAN
              CREDIT LICENCE{" "}
              {`${companyInfo?.creditRepNumber || DEFAULT_BROKER_DETAILS.creditRepNumber}`}
            </Typography>
            <Typography
              className={cx(
                classes.licenseInformation,
                "loan-app-font-size-12",
                "loan-app-font-weight-light",
                "loan-app-text-white",
                "loan-app-text-uppercase",
                classes.shownOnMobile
              )}
            >
              {`${companyInfo?.name || DEFAULT_BROKER_DETAILS.companyName}`} PTY LTD | ABN{" "}
              {`${companyInfo?.companyNumber || DEFAULT_BROKER_DETAILS.companyNumber}`}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            size={{
              xs: false,
              sm: 3,
            }}
          >
            <Box className={classes.siteBuiltBy}>
              <Typography
                component={Link}
                to="/loan-application/privacy"
                className={cx(
                  "loan-app-font-size-16",
                  "loan-app-font-weight-light",
                  "loan-app-text-white"
                )}
              >
                Privacy & Policy
              </Typography>
              {creditGuideFileUrl && (
                <>
                  &nbsp;&nbsp;&nbsp;
                  <Typography
                    component="a"
                    href={creditGuideFileUrl}
                    target="_blank"
                    className={cx(
                      "loan-app-font-size-16",
                      "loan-app-font-weight-light",
                      "loan-app-text-white"
                    )}
                  >
                    Credit Guide
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default LoanApplicationLayout;
